import type { AirlineLogo as AirlineLogoRow } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext, useMemo, useState } from "react";
import { airlineLogosOptions } from "../../lib/api/airlines";
import { airlineBadgeTone, airlineLogoCodes } from "../../lib/travel/airline-logos";

/** IATA code → its active airline's name and logo URL (null = no logo). A code with no airline is absent. */
export type AirlineLogoMap = ReadonlyMap<string, AirlineLogoRow>;

const EMPTY: AirlineLogoMap = new Map();

/**
 * The logos a SCREEN shows, in ONE `GET /airlines/logos` per distinct sorted
 * code set (react-query caches it) — never a request per row or per card.
 * Pass every carrier code the screen draws; order and duplicates do not matter.
 */
export function useAirlineLogos(codes: ReadonlyArray<string | null | undefined>): AirlineLogoMap {
	const key = airlineLogoCodes(codes);
	const logos = useQuery({ ...airlineLogosOptions(key), enabled: key !== "" });
	return useMemo(() => (logos.data ? new Map(logos.data.map((r) => [r.iata_code, r])) : EMPTY), [logos.data]);
}

const AirlineLogosContext = createContext<AirlineLogoMap>(EMPTY);

/** Hands a screen's `useAirlineLogos` map to every `AirlineLogo` under it. */
export function AirlineLogosProvider({ logos, children }: { logos: AirlineLogoMap; children: ReactNode }) {
	return <AirlineLogosContext.Provider value={logos}>{children}</AirlineLogosContext.Provider>;
}

/** The airline's name for a code, from the screen's one `useAirlineLogos` map (null = not known). */
export function useAirlineName(code: string | null | undefined): string | null {
	const map = useContext(AirlineLogosContext);
	return code ? (map.get(code.toUpperCase())?.name ?? null) : null;
}

/**
 * An airline's mark: its logo from the screen's map, else a neutral rounded
 * badge with the 2-character code (a tone picked by the code, so an airline
 * always looks the same). Decorative: the code / flight number is always
 * printed next to it, so it is hidden from screen readers.
 */
export function AirlineLogo({
	code,
	name,
	logoUrl,
	size = "md",
	className = "",
}: {
	code: string;
	/** The tooltip; the airline's name from the map wins. */
	name?: string | null;
	/** Overrides the screen's map (a list row that already carries its URL). */
	logoUrl?: string | null;
	size?: "sm" | "md" | "lg";
	className?: string;
}) {
	const map = useContext(AirlineLogosContext);
	const row = map.get(code.toUpperCase());
	const url = logoUrl !== undefined ? logoUrl : (row?.logo_url ?? null);
	const title = row?.name ?? name ?? undefined;
	// An image the browser could not load (an expired URL, a removed object) falls back to the badge.
	const [broken, setBroken] = useState<string | null>(null);
	const box = size === "sm" ? "size-[1.5rem] rounded-[0.3125rem]" : size === "lg" ? "size-[2.75rem] rounded-[0.5625rem]" : "size-[2rem] rounded-[0.4375rem]";

	if (url && broken !== url) {
		return (
			<span aria-hidden title={title} className={`inline-flex shrink-0 items-center justify-center overflow-hidden border border-gms-line bg-white ${box} ${className}`}>
				<img src={url} alt="" loading="lazy" decoding="async" onError={() => setBroken(url)} className="size-full object-contain p-[0.125rem]" />
			</span>
		);
	}
	return (
		<span
			aria-hidden
			title={title}
			className={`inline-flex shrink-0 items-center justify-center font-mono font-bold tracking-[0.02em] ${size === "sm" ? "text-[0.625rem]" : size === "lg" ? "text-[0.875rem]" : "text-[0.71875rem]"} ${box} ${airlineBadgeTone(code)} ${className}`}
		>
			{code.toUpperCase().slice(0, 2)}
		</span>
	);
}
